/**
 * Privacy Policy Page - UPDATED
 * 
 * GDPR-compliant privacy policy for WordExPress
 * ✅ ADDED: Generation limits and cleanup policy sections
 * Last Updated: February 10, 2026
 */

import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';

const LegalSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
}));

const PrivacyPolicy = () => {
  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 6,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" component="h1" fontWeight="bold" gutterBottom>
            Privacy Policy
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Last Updated: February 10, 2026
          </Typography>
        </Container>
      </Box>

      {/* Privacy Content */}
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body2">
            <strong>TL;DR:</strong> We collect only what's necessary to provide our service. We don't sell your data. You can request deletion anytime. We use industry-standard security. Free users get 2 themes/month, unpaid themes are deleted after 7 days. Read on for full details.
          </Typography>
        </Alert>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            1. Introduction
          </Typography>
          <Typography variant="body1" paragraph>
            WordExPress ("we", "us", or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our WordPress theme generation platform.
          </Typography>
          <Typography variant="body1" paragraph>
            By using WordExPress, you consent to the data practices described in this policy. If you do not agree with this policy, please do not use our service.
          </Typography>
          <Typography variant="body1">
            <strong>Data Controller:</strong> WordExPress is the data controller responsible for your personal information.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            2. Information We Collect
          </Typography>
          
          <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mt: 3 }}>
            2.1 Information You Provide
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Account Information:</strong>
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            • Name and email address (required for account creation)
            <br />
            • Password (encrypted and never stored in plain text)
            <br />
            • Optional profile information
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>Payment Information:</strong>
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            • Payment card details (processed securely by Stripe, not stored on our servers)
            <br />
            • Billing address
            <br />
            • Transaction history
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>Theme Generation Information:</strong>
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            • Industry and specialization selections
            <br />
            • Page selections and customization preferences
            <br />
            • Color schemes and layout choices
            <br />
            • Generated theme files
          </Typography>

          <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mt: 3 }}>
            2.2 Information Collected Automatically
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            • IP address and device information
            <br />
            • Browser type and version
            <br />
            • Operating system
            <br />
            • Pages visited and features used
            <br />
            • Time and date of visits
            <br />
            • Referral source
          </Typography>

          <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mt: 3 }}>
            2.3 Cookies and Tracking Technologies
          </Typography>
          <Typography variant="body1" paragraph>
            We use cookies and similar technologies to:
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3 }}>
            • Maintain your logged-in session
            <br />
            • Remember your preferences
            <br />
            • Analyze usage patterns
            <br />
            • Improve service performance
          </Typography>
        </LegalSection>

        {/* Fair Use & Generation Policy */}
        <LegalSection elevation={1} sx={{ bgcolor: '#f0f9ff' }}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            3. Fair Use & Generation Policy
          </Typography>
          
          <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mt: 3 }}>
            3.1 Generation Access
          </Typography>
          <Typography variant="body1" paragraph>
            WordExPress uses a pay-on-completion model for theme generation. You only pay when a theme is successfully generated and unlocked for download.
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            <strong>Quick Theme:</strong> $49 one-time per successful generation
            <br />
            <strong>Custom Theme:</strong> $149 one-time per successful generation
            <br />
            <strong>Studio Pack (if purchased):</strong> 3 Custom theme credits
            <br />
            <strong>Creator Access:</strong> Internal unlimited access (approved accounts only)
          </Typography>
          <Typography variant="body1" paragraph>
            Studio Pack credits are pre-purchased Custom generations and are applied only when a Custom generation succeeds.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>How Limits Work:</strong>
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            • You are charged only after successful generation and unlock
            <br />
            • Failed generations are not charged
            <br />
            • Studio Pack credits are consumed only for successful Custom generations
          </Typography>

          <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mt: 3 }}>
            3.2 Automatic Theme Cleanup Policy
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Unpaid Theme Deletion:</strong>
          </Typography>
          <Typography variant="body1" paragraph sx={{ pl: 3 }}>
            Completed themes that remain unpaid for <strong>7 days or longer</strong> are automatically deleted from our servers. This helps reduce storage costs for unfinished purchases.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>Failed Generation Cleanup:</strong>
          </Typography>
          <Typography variant="body1" paragraph sx={{ pl: 3 }}>
            Theme generations that fail or encounter errors are automatically deleted after <strong>3 days</strong> to free up storage space.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>Download Window:</strong>
          </Typography>
          <Typography variant="body1" paragraph sx={{ pl: 3 }}>
            After completing payment, you have <strong>90 days</strong> to download your purchased theme. After 90 days, theme files may be permanently deleted. We recommend downloading your themes immediately after purchase.
          </Typography>

          <Typography variant="h6" fontWeight="600" gutterBottom sx={{ mt: 3 }}>
            3.3 Changes to Limits
          </Typography>
          <Typography variant="body1" paragraph>
            We reserve the right to modify generation limits and cleanup policies at any time. Material changes will be communicated via email at least 30 days in advance.
          </Typography>
          <Typography variant="body1" paragraph>
            If pricing or credit policies change, updates will be published on our pricing page and in-product notices.
          </Typography>
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Important:</strong> Make sure to download your themes promptly after generation and payment. We are not responsible for themes deleted after the 7-day unpaid period or 90-day download window.
            </Typography>
          </Alert>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            4. How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We use your information to:
          </Typography>
          
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            <strong>Provide Our Service:</strong>
            <br />
            • Create and manage your account
            <br />
            • Generate WordPress themes based on your specifications
            <br />
            • Process one-time payments and optional credit-pack purchases
            <br />
            • Deliver generated themes to you
            <br />
            • Enforce generation limits and fair use policies
            <br />
            <br />
            <strong>Communicate With You:</strong>
            <br />
            • Send service-related notifications
            <br />
            • Respond to support requests
            <br />
            • Send billing and payment confirmations
            <br />
            • Provide important updates about our service
            <br />
            • Notify you when approaching generation limits
            <br />
            <br />
            <strong>Improve Our Service:</strong>
            <br />
            • Analyze usage patterns and trends
            <br />
            • Optimize theme generation algorithms
            <br />
            • Identify and fix technical issues
            <br />
            • Develop new features based on user feedback
            <br />
            <br />
            <strong>Legal and Security:</strong>
            <br />
            • Prevent fraud and abuse
            <br />
            • Comply with legal obligations
            <br />
            • Enforce our Terms of Service and usage limits
            <br />
            • Protect our rights and property
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            5. How We Share Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We do not sell, trade, or rent your personal information to third parties. We may share your information in these limited circumstances:
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>5.1 Service Providers:</strong>
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            • <strong>Stripe:</strong> Payment processing (subject to Stripe's privacy policy)
            <br />
            • <strong>Anthropic (Claude AI):</strong> Theme generation engine
            <br />
            • <strong>Cloud Hosting:</strong> Secure data storage and platform hosting
            <br />
            • <strong>Email Service:</strong> Transactional and support emails
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>5.2 Legal Requirements:</strong>
          </Typography>
          <Typography variant="body1" paragraph sx={{ pl: 3 }}>
            We may disclose information if required by law, court order, or government request, or to protect our rights, safety, or property.
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>5.3 Business Transfers:</strong>
          </Typography>
          <Typography variant="body1" sx={{ pl: 3 }}>
            In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity with notice to you.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            6. Data Security
          </Typography>
          <Typography variant="body1" paragraph>
            We implement industry-standard security measures to protect your information:
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            • <strong>Encryption:</strong> HTTPS/TLS encryption for data in transit
            <br />
            • <strong>Secure Storage:</strong> Encrypted databases and secure cloud infrastructure
            <br />
            • <strong>Access Controls:</strong> Limited employee access on a need-to-know basis
            <br />
            • <strong>Password Security:</strong> Passwords hashed using bcrypt
            <br />
            • <strong>Regular Audits:</strong> Security assessments and vulnerability testing
            <br />
            • <strong>Monitoring:</strong> Continuous monitoring for suspicious activity
          </Typography>
          <Typography variant="body1">
            While we strive to protect your information, no method of transmission or storage is 100% secure. You are responsible for maintaining the confidentiality of your login credentials.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            7. Data Retention
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>7.1 Account Data:</strong> We retain your account information for as long as your account is active or as needed to provide services.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>7.2 Generated Themes:</strong>
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            • <strong>Unpaid themes:</strong> Deleted after 7 days (free users only)
            <br />
            • <strong>Failed generations:</strong> Deleted after 3 days
            <br />
            • <strong>Completed & paid themes:</strong> Available for download for 90 days
            <br />
            • <strong>After 90 days:</strong> Theme files may be permanently deleted
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>7.3 Financial Records:</strong> Payment and billing information is retained for 7 years to comply with tax and legal requirements.
          </Typography>
          <Typography variant="body1">
            <strong>7.4 Logs and Analytics:</strong> Usage logs and analytics data are retained for up to 2 years for service improvement and security purposes.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            8. Your Privacy Rights
          </Typography>
          <Typography variant="body1" paragraph>
            Depending on your location, you may have the following rights:
          </Typography>
          
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            <strong>• Access:</strong> Request a copy of your personal information
            <br />
            <strong>• Correction:</strong> Update or correct inaccurate information
            <br />
            <strong>• Deletion:</strong> Request deletion of your personal information
            <br />
            <strong>• Portability:</strong> Receive your data in a machine-readable format
            <br />
            <strong>• Objection:</strong> Object to certain processing activities
            <br />
            <strong>• Restriction:</strong> Request restriction of processing
            <br />
            <strong>• Withdraw Consent:</strong> Withdraw previously given consent
          </Typography>
          
          <Typography variant="body1" paragraph>
            <strong>Exercising Your Rights:</strong>
          </Typography>
          <Typography variant="body1" sx={{ pl: 3 }}>
            To exercise these rights, contact us at privacy@getwordexpress.com. We will respond within 30 days. You may also manage certain information through your account settings.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            9. GDPR Compliance (EU Users)
          </Typography>
          <Typography variant="body1" paragraph>
            If you are in the European Economic Area (EEA), we process your data under the following legal bases:
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            • <strong>Contract Performance:</strong> Processing necessary to provide our service
            <br />
            • <strong>Consent:</strong> Where you have given explicit consent
            <br />
            • <strong>Legitimate Interests:</strong> For service improvement and fraud prevention
            <br />
            • <strong>Legal Obligations:</strong> Compliance with laws and regulations
          </Typography>
          <Typography variant="body1">
            You have the right to lodge a complaint with your local data protection authority if you believe we have not complied with GDPR.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            10. CCPA Compliance (California Users)
          </Typography>
          <Typography variant="body1" paragraph>
            California residents have additional rights under the California Consumer Privacy Act (CCPA):
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3, mb: 2 }}>
            • Right to know what personal information is collected
            <br />
            • Right to know if personal information is sold or disclosed
            <br />
            • Right to opt-out of the sale of personal information
            <br />
            • Right to deletion of personal information
            <br />
            • Right to non-discrimination for exercising CCPA rights
          </Typography>
          <Typography variant="body1">
            <strong>Important:</strong> We do not sell personal information.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            11. International Data Transfers
          </Typography>
          <Typography variant="body1" paragraph>
            Your information may be transferred to and processed in countries other than your own. We ensure adequate safeguards are in place, including:
          </Typography>
          <Typography variant="body1" component="div" sx={{ pl: 3 }}>
            • Standard Contractual Clauses approved by the European Commission
            <br />
            • Adequacy decisions for data transfers
            <br />
            • Processor agreements with service providers
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            12. Children's Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            WordExPress is not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18.
          </Typography>
          <Typography variant="body1">
            If we discover we have collected information from a child under 18, we will delete it immediately. If you believe we have collected information from a child, please contact us at privacy@getwordexpress.com.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            13. Third-Party Links
          </Typography>
          <Typography variant="body1" paragraph>
            Our service may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies before providing any personal information.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            14. Changes to This Privacy Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last Updated" date.
          </Typography>
          <Typography variant="body1" paragraph>
            For material changes, we will provide notice via email or prominent platform notification at least 30 days before changes take effect.
          </Typography>
          <Typography variant="body1">
            Continued use of the service after changes constitutes acceptance of the updated Privacy Policy.
          </Typography>
        </LegalSection>

        <LegalSection elevation={1}>
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            15. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            For questions or concerns about this Privacy Policy or our data practices, please contact us:
          </Typography>
          <Typography variant="body1" component="div">
            <strong>Email:</strong> privacy@getwordexpress.com
            <br />
            <strong>Support:</strong> support@getwordexpress.com
            <br />
            <strong>Data Protection Officer:</strong> dpo@getwordexpress.com
            <br />
            <strong>Website:</strong> www.getwordexpress.com
          </Typography>
        </LegalSection>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="body2" color="textSecondary">
            By using WordExPress, you acknowledge that you have read and understood this Privacy Policy.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;